import {
  Box,
  Stack,
  Paper,
  Typography,
  FormControl,
  TextField,
  InputAdornment,
  SelectChangeEvent,
  Button,
  IconButton,
} from '@mui/material';
import i18n from '../../libs/language';
import { useService } from '@old/hooks/use-service';
import { BaseTable } from '@old/ui/table';
import { CustomDetailPanelToggle } from '@old/ui/table-detail';
import SearchIcon from '@old/assets/svg/search.svg';
import { GridColDef, GRID_DETAIL_PANEL_TOGGLE_COL_DEF, DataGridProProps } from '@mui/x-data-grid-pro';
import { useGlobalStore } from '@old/global-store/use-store';
import { stylesInput } from '@old/styles/generic-styles';
import { Selector } from '@old/ui/selector/selector';
import { getInconsistenciesResult } from '@old/services/domain/page/inconsistencies/get-inconsistencies-results';
import { listCountrysideService } from '@old/services/domain/base/countryside/countryside-list';
import { useMemo, useRef } from 'react';
import React from 'react';
import { useEffectOnUpdate } from '@old/hooks/core';
// (emi) TODO: probablemente este formatDateTime se pueda cambiar por un dayjs (con el plugin de UTC) para transformar fechas y horas.
import { formatDateTime } from '@old/utils/helper';
import ChevronRightIcon from '@old/assets/svg/core/chevron-right-blue.svg';
import SupportIcon from '@old/assets/svg/support-red.svg';
import { useNavigate } from 'react-router';
import { PATH } from '@old/data/core/paths';
import { InconsistenciesObservationModal } from '@old/features/inconsistencies/inconsistencies-modal';
import { useState } from 'react';
import { ToastAction } from '@old/ui/toast/toast-action';
import RelojIcon from '@old/assets/svg/reloj.svg';
import DeleteIcon from '@old/assets/svg/core/trash-red.svg';

type Discrepancy = Awaited<
  ReturnType<typeof getInconsistenciesResult>
>['summary']['data']['0']['discrepancies']['0'];

enum DiscrepancyDataSource {
  BAGGING_DATE = 'BAGGING_DATE', // Fecha de embolsado YYYY-MM-DD
  BAGGING_DATE_CHECKED = 'BAGGING_DATE_CHECKED', // True si la fecha de embolsaod está certificada / False si tiene sat pero no está dentro dle rango de embolsado // null: sB mobile pero no tiene servicio SAT
  SAT_BAGGING_START_DATE = 'SAT_BAGGING_START_DATE', // Fecha de embolsado de sat YYYY-MM-DD
  SAT_BAGGING_END_DATE = 'SAT_BAGGING_END_DATE', // Fecha de embolsado de sat YYYY-MM-DD
  SAT_CLOSING_DATE = 'SAT_CLOSING_DATE', // Fecha de cierre de sat YYYY-MM-DD
  WEIGHT_KG = 'WEIGHT_KG', // Peso de entrada KG
  SIZE_M = 'SIZE_M', // Largo MTS
  MIN_FILLING_FACTOR = 'MIN_FILLING_FACTOR', // Factor de llenado minimo
  MAX_FILLING_FACTOR = 'MAX_FILLING_FACTOR', // Factor de llenado máximo
  MIN_FILLING_FACTOR_CORRECTED = 'MIN_FILLING_FACTOR_CORRECTED', // Factor de llenado mínimo corregido (considera peso y tamaño)
  MAX_FILLING_FACTOR_CORRECTED = 'MAX_FILLING_FACTOR_CORRECTED', // Factor de llenado máximo corregido (considera peso y tamaño)
  DIAMETER_F = 'DIAMETER_F', // Diámetro pies
  HARVEST_START_YEAR = 'HARVEST_START_YEAR', // Año de cosecha mínimo YYYY-MM-DD
  HARVEST_END_YEAR = 'HARVEST_END_YEAR', // Año de cosecha máxima YYYY-MM-DD
  HAS_CLOSING_EVENT = 'HAS_CLOSING_EVENT', // Evento de cierre true/false
  HAS_NONEXISTENCE_EVENT = 'HAS_NONEXISTENCE_EVENT', // Evento de inexistencia true/false
  OVERLAPPED_RAW_DATA = 'OVERLAPPED_RAW_DATA', // Overlapped silobags (regla inactiva, pero ya traducida)
  HAS_CLOSING_EVENT_CREATED_AT = 'HAS_CLOSING_EVENT_CREATED_AT', // Fecha de creación del evento de cierre
  HAS_NONEXISTENCE_EVENT_CREATED_AT = 'HAS_NONEXISTENCE_EVENT_CREATED_AT', // Fecha de creación del evento de inexistencia

  // TIC-3909: claves de `sources` de las reglas v2 (largo y peso contra la medición satelital).
  // El sub-detalle traduce clave por clave, así que cada una necesita su entrada en el Record de abajo.
  CURRENT_WEIGHT_KG = 'CURRENT_WEIGHT_KG', // Stock actual = peso embolsado - extracciones
  INITIAL_VERIFIED_SIZE_M = 'INITIAL_VERIFIED_SIZE_M', // Largo inicial medido por satélite
  CURRENT_VERIFIED_SIZE_M = 'CURRENT_VERIFIED_SIZE_M', // Largo actual medido por satélite
  CORRECTION_FACTOR = 'CORRECTION_FACTOR', // diámetro² / 81
  INITIAL_SAT_VIEW_ID = 'INITIAL_SAT_VIEW_ID',
  INITIAL_SAT_TIMESTAMP = 'INITIAL_SAT_TIMESTAMP',
  CURRENT_SAT_VIEW_ID = 'CURRENT_SAT_VIEW_ID',
  CURRENT_SAT_TIMESTAMP = 'CURRENT_SAT_TIMESTAMP',
  SAT_IMAGES_COUNT = 'SAT_IMAGES_COUNT',
  INITIAL_LENGTH_SOURCE = 'INITIAL_LENGTH_SOURCE', // BAGGING_EVENT | NOT_AVAILABLE
  SPECIES_FILLING_FACTOR_TN = 'SPECIES_FILLING_FACTOR_TN', // Coeficiente de la especie aplicado (Ton/m)
  INITIAL_ESTIMATED_WEIGHT_KG = 'INITIAL_ESTIMATED_WEIGHT_KG',
  INITIAL_ESTIMATED_WEIGHT_MIN_KG = 'INITIAL_ESTIMATED_WEIGHT_MIN_KG',
  INITIAL_ESTIMATED_WEIGHT_MAX_KG = 'INITIAL_ESTIMATED_WEIGHT_MAX_KG',
  CURRENT_ESTIMATED_WEIGHT_KG = 'CURRENT_ESTIMATED_WEIGHT_KG',
  CURRENT_ESTIMATED_WEIGHT_MIN_KG = 'CURRENT_ESTIMATED_WEIGHT_MIN_KG',
  CURRENT_ESTIMATED_WEIGHT_MAX_KG = 'CURRENT_ESTIMATED_WEIGHT_MAX_KG',
  DECLARED_ESTIMATED_WEIGHT_KG = 'DECLARED_ESTIMATED_WEIGHT_KG',
  DECLARED_ESTIMATED_WEIGHT_MIN_KG = 'DECLARED_ESTIMATED_WEIGHT_MIN_KG',
  DECLARED_ESTIMATED_WEIGHT_MAX_KG = 'DECLARED_ESTIMATED_WEIGHT_MAX_KG',
  LENGTH_DIFF_PCT = 'LENGTH_DIFF_PCT',
  WEIGHT_DIFF_PCT = 'WEIGHT_DIFF_PCT',
  TOLERANCE_PCT = 'TOLERANCE_PCT',
  MIN_ABS_M = 'MIN_ABS_M',
  SIZE_M_VALID = 'SIZE_M_VALID',
  HAS_EXTRACTION_BEFORE_INITIAL_IMAGE = 'HAS_EXTRACTION_BEFORE_INITIAL_IMAGE',
  SUSPECTED_FIELD = 'SUSPECTED_FIELD', // WEIGHT_KG | SIZE_M | CURRENT_WEIGHT_KG | BAGGING_DATE
  DECLARED_BAGGING_DATE = 'DECLARED_BAGGING_DATE',
  BAGGING_EVENT_ID = 'BAGGING_EVENT_ID', // id de sat_view_events (tipo 1, embolsado)
  SAT_BAGGING_FROM = 'SAT_BAGGING_FROM',
  SAT_BAGGING_TO = 'SAT_BAGGING_TO',
  SAT_BAGGING_BEFORE_SAT_VIEW_ID = 'SAT_BAGGING_BEFORE_SAT_VIEW_ID',
  SAT_BAGGING_AFTER_SAT_VIEW_ID = 'SAT_BAGGING_AFTER_SAT_VIEW_ID',
  TOLERANCE_DAYS = 'TOLERANCE_DAYS',
  DAYS_OUTSIDE_RANGE = 'DAYS_OUTSIDE_RANGE',
}
/**
 * Las claves de `sources` se traducen una por una con `app.inconsistencies_page.<CLAVE>`, y el nombre
 * de la clave i18n es siempre igual al del data source. Derivar el mapa del enum evita que una clave
 * nueva (TIC-3909 agregó ~35) quede sin entrada y se muestre cruda en el sub-detalle.
 */
const discrepancySourceTranslations = Object.fromEntries(
  Object.values(DiscrepancyDataSource).map((source) => [source, i18n.t(`app.inconsistencies_page.${source}`)])
) as Record<DiscrepancyDataSource, string>;

/** Data sources cuyo valor es una fecha y se muestra con `formatDateTime`. */
const DATE_SOURCES = new Set<DiscrepancyDataSource>([
  DiscrepancyDataSource.HAS_CLOSING_EVENT_CREATED_AT,
  DiscrepancyDataSource.HAS_NONEXISTENCE_EVENT_CREATED_AT,
  DiscrepancyDataSource.BAGGING_DATE,
  DiscrepancyDataSource.BAGGING_DATE_CHECKED,
  DiscrepancyDataSource.SAT_BAGGING_START_DATE,
  DiscrepancyDataSource.SAT_BAGGING_END_DATE,
  // TIC-3909
  DiscrepancyDataSource.INITIAL_SAT_TIMESTAMP,
  DiscrepancyDataSource.CURRENT_SAT_TIMESTAMP,
  DiscrepancyDataSource.DECLARED_BAGGING_DATE,
  DiscrepancyDataSource.SAT_BAGGING_FROM,
  DiscrepancyDataSource.SAT_BAGGING_TO,
]);

/**
 * TIC-4306: data sources que no se muestran en el sub-detalle. El peso estimado medio (punto medio de
 * la banda) confunde: la regla valida contra el rango [MIN, MAX], así que solo se muestran esos dos.
 * Se filtra acá porque el sub-detalle recorre todas las claves de `sources` que manda el backend.
 */
const HIDDEN_SOURCES = new Set<string>([
  DiscrepancyDataSource.INITIAL_ESTIMATED_WEIGHT_KG,
  DiscrepancyDataSource.CURRENT_ESTIMATED_WEIGHT_KG,
  DiscrepancyDataSource.DECLARED_ESTIMATED_WEIGHT_KG,
]);

export function InconsistenciesPage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { filtersInconsistencies, setFiltersInconsistencies, organizationId } = useGlobalStore();
  const queryCountrysideList = { organization_id: organizationId };
  const {
    summary: countrysides,
    isLoading: isLoadingCountryside,
    trigger: getCountrysides,
  } = useService(listCountrysideService, {
    fetchOnMount: [{ query: queryCountrysideList }],
  });
  useEffectOnUpdate(() => {
    getCountrysides({ query: queryCountrysideList });
  }, [organizationId]);
  const [openObservationModal, setOpenObservationModal] = useState<boolean>(false);
  const [successObservations, setSuccessObservations] = useState<boolean>(false);
  const [resultId, setResultId] = useState<number>(0);

  const {
    summary: inconsistenciesResult,
    isLoading,
    trigger: getResults,
  } = useService(getInconsistenciesResult, {
    fetchOnMount: true,
  });
  const totals = inconsistenciesResult?.summary;
  const rows = inconsistenciesResult?.data.map((item, index) => ({ ...item, id: index }));

  const filteredRows = useMemo(() => {
    let result = rows || [];

    if (filtersInconsistencies.silobagName) {
      const searchText = filtersInconsistencies.silobagName.toLowerCase();
      result = result.filter((row) => row.silobag_name.toLowerCase().includes(searchText));
    }
    // Filtro por countrysidesIds
    if (filtersInconsistencies.countrysidesIds.length > 0) {
      const selectedCountrysideNames = (countrysides || [])
        .filter((c) => filtersInconsistencies.countrysidesIds.includes(c.id))
        .map((c) => c.name);
      result = result.filter((row) => selectedCountrysideNames.includes(row.countryside));
    }

    // Filtro por typeCodes (si aplica)
    if (filtersInconsistencies?.typeCodes?.length > 0) {
      result = result.filter((row) =>
        row.discrepancies.some((discrepancy) => filtersInconsistencies.typeCodes.includes(discrepancy.code))
      );
    }

    return result;
  }, [
    rows,
    filtersInconsistencies.countrysidesIds,
    filtersInconsistencies.typeCodes,
    filtersInconsistencies.silobagName,
    countrysides,
  ]);

  //handlers y funciones de los filtros
  const handlerCountrysideID = (e: SelectChangeEvent<number[] | unknown>) => {
    const selectedValue = e.target.value as number[];
    setFiltersInconsistencies({ ...filtersInconsistencies, countrysidesIds: selectedValue });
  };
  const deleteCountrysidesFilter = () => {
    setFiltersInconsistencies({ ...filtersInconsistencies, countrysidesIds: [] });
  };

  const handlerLevelID = (e: SelectChangeEvent<number[] | unknown>) => {
    const selectedValue = e.target.value as number[];
    setFiltersInconsistencies({ ...filtersInconsistencies, levelIds: selectedValue });
  };
  const deleteLevelFilter = () => {
    setFiltersInconsistencies({ ...filtersInconsistencies, levelIds: [] });
  };

  const handlerTypeCode = (e: SelectChangeEvent<string[] | unknown>) => {
    const selectedValue = e.target.value as string[];
    setFiltersInconsistencies({ ...filtersInconsistencies, typeCodes: selectedValue });
  };

  const deleteTypeFilter = () => {
    setFiltersInconsistencies({ ...filtersInconsistencies, typeCodes: [] });
  };
  //hooks y callbacks
  useEffectOnUpdate(() => {
    getResults();
  }, [filtersInconsistencies, organizationId]);
  //columnas principales

  const inconsistencyTypes = [
    {
      id: 'DISC_BAGGING_DATE_OUT_OF_RANGE',
      name: i18n.t('app.inconsistencies_page.DISC_BAGGING_DATE_OUT_OF_RANGE'),
    },
    {
      id: 'DISC_FILLING_FACTOR_OUT_OF_RANGE',
      name: i18n.t('app.inconsistencies_page.DISC_FILLING_FACTOR_OUT_OF_RANGE'),
    },
    {
      id: 'DISC_HARVEST_DATE_OUT_OF_RANGE',
      name: i18n.t('app.inconsistencies_page.DISC_HARVEST_DATE_OUT_OF_RANGE'),
    },
    {
      id: 'DISC_NONE_EXISTENCE_EVENT',
      name: i18n.t('app.inconsistencies_page.DISC_NONE_EXISTENCE_EVENT'),
    },
    // TIC-3909: reglas v2 contra la medición satelital
    {
      id: 'DISC_LENGTH_INCONSISTENCY',
      name: i18n.t('app.inconsistencies_page.DISC_LENGTH_INCONSISTENCY'),
    },
    {
      id: 'DISC_INITIAL_WEIGHT_INCONSISTENCY',
      name: i18n.t('app.inconsistencies_page.DISC_INITIAL_WEIGHT_INCONSISTENCY'),
    },
    {
      id: 'DISC_CURRENT_WEIGHT_INCONSISTENCY',
      name: i18n.t('app.inconsistencies_page.DISC_CURRENT_WEIGHT_INCONSISTENCY'),
    },
    // TIC-3909: la regla de fecha de embolsado quedó consolidada en la legacy `BAGGING_DATE` (id 1),
    // que sigue emitiendo `DISC_BAGGING_DATE_OUT_OF_RANGE` (ya listado arriba) con los `sources` v2.
  ];
  const columns: GridColDef[] = [
    {
      field: 'silobag_name',
      display: 'flex',
      headerName: 'Silo',
      flex: 1.3,
      renderCell: (params) => {
        const siloId = params.row.silobag_id;
        return (
          <Box
            sx={{
              textDecoration: 'none',
              color: 'black',
              marginBottom: '5px',
              cursor: 'pointer',
              fontSize: '.85rem',

              '&:hover': {
                color: '#22a7f0',
              },
              textOverflow: 'ellipsis',
            }}
            onClick={() => navigate(`${PATH.SILOBAG}/${siloId}`)}
          >
            {params.value}{' '}
            <span>
              <img src={ChevronRightIcon} style={{ width: 8, height: 8, marginLeft: 4 }} />
            </span>
          </Box>
        );
      },
    },
    { field: 'silobag_id', headerName: 'Id', flex: 1 },
    { field: 'countryside', headerName: i18n.t('app.countryside'), flex: 1 },
    { field: 'total_discrepancies', headerName: 'Total', flex: 1 },
    {
      field: 'discrepancies3',
      display: 'flex',
      headerName: i18n.t('app.inconsistencies_page.g_high'),
      flex: 1,
      renderHeader: () => (
        <Box display="flex" alignItems="center">
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 13,
              height: 12,
              backgroundColor: 'red',
              borderRadius: '50%',
              marginRight: 1,
            }}
          />
          {i18n.t('app.inconsistencies_page.g_high')}
        </Box>
      ),
      renderCell: (params) => {
        return <span style={{ textAlign: 'center' }}>{params.row.discrepancies_counters?.['3'] ?? 0}</span>;
      },
    },
    {
      field: 'discrepancies2',
      display: 'flex',
      headerName: i18n.t('app.inconsistencies_page.g_medium'),
      flex: 1,
      renderHeader: () => (
        <Box display="flex" alignItems="center">
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 13,
              height: 12,
              backgroundColor: 'orange',
              borderRadius: '50%',
              marginRight: 1,
            }}
          />
          {i18n.t('app.inconsistencies_page.g_medium')}
        </Box>
      ),
      renderCell: (params) => {
        return <span>{params.row.discrepancies_counters?.['2'] ?? 0}</span>;
      },
    },
    {
      field: 'discrepancies1',
      display: 'flex',
      headerName: i18n.t('app.inconsistencies_page.g_low'),
      flex: 1,
      renderHeader: () => (
        <Box display="flex" alignItems="center">
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 13,
              height: 12,
              backgroundColor: '#6baa00',
              borderRadius: '50%',
              marginRight: 1,
            }}
          />
          {i18n.t('app.inconsistencies_page.g_low')}
        </Box>
      ),
      renderCell: (params) => {
        return <span>{params.row.discrepancies_counters?.['1'] ?? 0}</span>;
      },
    },
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: CustomDetailPanelToggle,
      flex: 1,
    },
  ];
  //columnas de ver detalle
  const columnsDetail: GridColDef[] = [
    {
      field: 'test_date',
      display: 'flex',
      headerName: i18n.t('app.date'),
      flex: 1.2,
      renderCell: (params) => <div>{formatDateTime(params.value, 3)}</div>,
    },
    {
      field: 'level',
      display: 'flex',
      headerName: i18n.t('app.inconsistencies_page.gravity'),
      flex: 1,
      renderCell: (params) => {
        const gravityLevel = params.value;
        const gravityValues: { [key: number]: { status: string; color: string } } = {
          1: { status: 'Baja', color: '#6baa00' },
          2: { status: 'Media', color: 'orange' },
          3: { status: 'Alta', color: 'red' },
        };
        return (
          <Box display="flex" alignItems="center">
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 13,
                height: 12,
                backgroundColor: gravityValues[gravityLevel]?.color,
                borderRadius: '50%',
                marginRight: 1,
              }}
            />
            {gravityValues[gravityLevel]?.status}
          </Box>
        );
      },
    },
    {
      field: 'message',
      display: 'flex',
      headerName: i18n.t('app.inconsistencies_page.message'),
      flex: 4.2,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            overflow: 'hidden',
            fontSize: '12.5px',
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: 'support',
      display: 'flex',
      headerName: 'Soporte',
      headerAlign: 'center',
      flex: 1,
      align: 'center',
      renderCell: (params) => (
        <Box>
          <IconButton
            sx={{ paddingBottom: 2 }}
            onClick={() => {
              setResultId(params.row.result_id);
              setOpenObservationModal(true);
            }}
            disabled={filtersInconsistencies.solved}
          >
            {!filtersInconsistencies.solved ? (
              <img src={SupportIcon} alt="chevron-right-icon" style={{ width: 20, height: 20 }} />
            ) : null}
          </IconButton>
        </Box>
      ),
    },
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: CustomDetailPanelToggle,
      flex: 2,
    },
  ];

  const columnsDetailSolved: GridColDef[] = [
    ...columnsDetail.slice(0, 3),
    {
      field: 'solved_at',
      display: 'flex',
      headerName: 'Fecha resolución',
      flex: 1.5,
      renderCell: (params) => <div>{params.value ? formatDateTime(params.value, 3) : '-'}</div>,
    },
    {
      field: 'user',
      display: 'flex',
      headerName: 'Usuario',
      flex: 1.5,
      renderCell: (params) => <div style={{ textAlign: 'center' }}>{params.value.username ?? '-'}</div>,
    },
    ...columnsDetail.slice(3),
  ];

  // Detalle del detalle (segundo nivel)
  const columnsSubDetail: GridColDef[] = [
    {
      field: 'source',
      display: 'flex',
      headerName: 'Fuente',
      flex: 1,
      renderCell: (params) => {
        const translatedValue =
          discrepancySourceTranslations[params.value as DiscrepancyDataSource] || params.value;
        return <div>{translatedValue}</div>;
      },
    },
    {
      field: 'value',
      display: 'flex',
      headerName: 'Valor',
      flex: 1,
      renderCell: (params) => {
        const source = params.row.source as DiscrepancyDataSource;
        const value = DATE_SOURCES.has(source) ? formatDateTime(params.value, 3) : params.value;
        return <div>{value}</div>;
      },
    },
  ];

  // Función para obtener el contenido del detalle del detalle
  const getSubDetailPanelContent = React.useCallback<NonNullable<DataGridProProps['getDetailPanelContent']>>(
    ({ row }) => {
      const subDetailRows = Object.entries(row.sources || {})
        .filter(([key]) => !HIDDEN_SOURCES.has(key))
        .map(([key, value], index) => ({
          id: index,
          source: key,
          value: value,
        }));

      return (
        <Stack sx={{ borderBottom: '1px solid #E6E6E6' }}>
          <BaseTable
            rows={subDetailRows}
            hideFooter
            columns={columnsSubDetail}
            initialPageSize={20}
            noBorders
            rowHeight={45}
          />
        </Stack>
      );
    },
    []
  );

  //Contenido(tabla) del panel de detalle
  const getDetailPanelContent = React.useCallback<NonNullable<DataGridProProps['getDetailPanelContent']>>(
    ({ row }) => (
      <Stack sx={{ borderBottom: '1px solid #E6E6E6' }}>
        <BaseTable
          rows={
            row.discrepancies.map((item: Discrepancy, index: number) => ({
              ...item,
              id: index,
            })) ?? []
          }
          hideFooter
          columns={filtersInconsistencies.solved ? columnsDetailSolved : columnsDetail}
          initialPageSize={10}
          noBorders
          rowHeight={70}
          getDetailPanelContent={getSubDetailPanelContent}
        />
      </Stack>
    ),
    [filtersInconsistencies.solved]
  );

  const hasFilters = Object.values(filtersInconsistencies).some((value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'boolean') return value !== false;
    return value !== '';
  });

  const handleRemoveFilters = () => {
    setFiltersInconsistencies({
      silobagId: '',
      silobagName: '',
      countrysidesIds: [],
      levelIds: [],
      typeCodes: [],
      ruleIds: [],
      solved: false,
    });
  };

  return (
    <Box style={{ width: '95%', margin: 'auto' }}>
      <Stack justifyContent={'space-between'} direction={'row'} alignItems={'center'}>
        <Box component="h1" fontWeight={500} style={{ color: '#49454F' }}>
          {i18n.t('app.inconsistencies_page.inconsistencies')}
        </Box>
      </Stack>
      <Paper elevation={0} style={{ padding: '20px 40px', borderRadius: '10px' }}>
        <Stack spacing={4.5} direction={'row'}>
          <Typography variant={'h6'} fontSize={30} fontWeight={500}>
            {totals?.total_silobags ?? 0} <span style={{ fontSize: 20 }}>{i18n.t('app.silobags')}</span>
          </Typography>

          <Typography
            variant={'h6'}
            fontSize={30}
            fontWeight={500}
            paddingRight={4}
            borderRight={{ xs: 'none', md: '2.6px solid #d3d0d0' }}
          >
            {totals?.total_input_weight ? (+totals.total_input_weight / 1000).toFixed(0) : 0}{' '}
            <span style={{ fontSize: 16, fontWeight: 400 }}>Toneladas</span>
          </Typography>

          <Typography variant={'h6'} fontSize={30} fontWeight={500}>
            {totals?.total_discrepancies ?? 0}{' '}
            <span style={{ fontSize: 16, fontWeight: 400, marginBottom: 0.3 }}>
              {i18n.t('app.inconsistencies_page.totals')}
            </span>
          </Typography>

          <Typography variant={'h6'} fontSize={30} fontWeight={500}>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 13,
                height: 12,
                backgroundColor: 'red',
                borderRadius: '50%',
                marginRight: 1,
                marginBottom: 0.3,
              }}
            />
            {totals?.discrepancies_counters['3'] ?? 0}{' '}
            <span style={{ fontSize: 16, fontWeight: 400 }}>
              {i18n.t('app.inconsistencies_page.gravity_high')}
            </span>
          </Typography>

          <Typography variant={'h6'} fontSize={30} fontWeight={500}>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 13,
                height: 12,
                backgroundColor: 'orange',
                borderRadius: '50%',
                marginRight: 1,
                marginBottom: 0.3,
              }}
            />
            {totals?.discrepancies_counters['2'] ?? 0}{' '}
            <span style={{ fontSize: 16, fontWeight: 400 }}>
              {i18n.t('app.inconsistencies_page.gravity_medium')}
            </span>
          </Typography>

          <Typography variant={'h6'} fontSize={30} fontWeight={500}>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 13,
                height: 12,
                backgroundColor: '#6baa00',
                borderRadius: '50%',
                marginRight: 1,
                marginBottom: 0.3,
              }}
            />
            {totals?.discrepancies_counters['1'] ?? 0}{' '}
            <span style={{ fontSize: 16, fontWeight: 400 }}>
              {i18n.t('app.inconsistencies_page.gravity_low')}
            </span>
          </Typography>
        </Stack>
      </Paper>
      <Box sx={{ marginTop: 3 }}>
        <Stack direction={'row'} gap={1}>
          <FormControl style={{ width: '20%', fontSize: 14, marginRight: '10px', height: '100%' }}>
            <TextField
              size="medium"
              variant="outlined"
              placeholder={i18n.t('app.silobag_list.search_by_name_id')}
              style={stylesInput}
              value={filtersInconsistencies.silobagName}
              onChange={(e) => {
                setFiltersInconsistencies({ ...filtersInconsistencies, silobagName: e.target.value });
              }}
              inputRef={inputRef}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <img src={SearchIcon} alt="search-icon" />
                    </InputAdornment>
                  ),
                  inputProps: { style: { fontSize: 13, height: 22 } },
                },
              }}
            />
          </FormControl>
          <FormControl style={{ width: '20%', fontSize: 14, marginRight: '10px', height: '100%' }}>
            <Selector
              label={i18n.t('app.countrysides')}
              id="countryside_id"
              value={filtersInconsistencies?.countrysidesIds || []}
              onChange={(e) => handlerCountrysideID(e)}
              multiple
              options={countrysides}
              selectedList={countrysides?.filter((countryside) =>
                filtersInconsistencies?.countrysidesIds?.includes(countryside.id)
              )}
              renderValue={() =>
                countrysides
                  ?.filter((countryside) => filtersInconsistencies?.countrysidesIds?.includes(countryside.id))
                  .map((matchedCountryside) => matchedCountryside.name)
                  .join(', ')
              }
              deleteFilter={deleteCountrysidesFilter}
              disabled={isLoadingCountryside}
            />
          </FormControl>
          <FormControl style={{ width: '20%', fontSize: 14, marginRight: '10px', height: '100%' }}>
            <Selector
              label={i18n.t('app.inconsistencies_page.severity')}
              id="severity_level"
              value={filtersInconsistencies?.levelIds || []}
              onChange={(e) => handlerLevelID(e)}
              multiple
              options={[
                { id: 3, name: i18n.t('app.inconsistencies_page.g_high') },
                { id: 2, name: i18n.t('app.inconsistencies_page.g_medium') },
                { id: 1, name: i18n.t('app.inconsistencies_page.g_low') },
              ]}
              selectedList={[
                { id: 3, name: i18n.t('app.inconsistencies_page.g_high') },
                { id: 2, name: i18n.t('app.inconsistencies_page.g_medium') },
                { id: 1, name: i18n.t('app.inconsistencies_page.g_low') },
              ].filter((level) => filtersInconsistencies?.levelIds?.includes(level.id))}
              renderValue={() =>
                [
                  { id: 3, name: i18n.t('app.inconsistencies_page.g_high') },
                  { id: 2, name: i18n.t('app.inconsistencies_page.g_medium') },
                  { id: 1, name: i18n.t('app.inconsistencies_page.g_low') },
                ]
                  .filter((level) => filtersInconsistencies?.levelIds?.includes(level.id))
                  .map((level) => level.name)
                  .join(', ')
              }
              deleteFilter={deleteLevelFilter}
            />
          </FormControl>

          <FormControl style={{ width: '20%', fontSize: 14, marginRight: '10px', height: '100%' }}>
            <Selector
              label={i18n.t('app.inconsistencies_page.type')}
              id="inconsistency_type"
              value={filtersInconsistencies?.typeCodes || []}
              onChange={handlerTypeCode}
              multiple
              options={inconsistencyTypes}
              selectedList={inconsistencyTypes.filter((type) =>
                filtersInconsistencies?.typeCodes?.includes(type.id)
              )}
              renderValue={() =>
                inconsistencyTypes
                  .filter((type) => filtersInconsistencies?.typeCodes?.includes(type.id))
                  .map((type) => type.name)
                  .join(', ')
              }
              deleteFilter={deleteTypeFilter}
            />
          </FormControl>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '20px',
              marginLeft: '20px',
              cursor: 'pointer',
            }}
            onClick={() => {
              setFiltersInconsistencies({
                ...filtersInconsistencies,
                solved: !filtersInconsistencies.solved,
              });
            }}
          >
            <img src={RelojIcon} alt="" />
            <Typography
              fontWeight={500}
              sx={{
                marginLeft: '5px',

                fontSize: '14px',
                color: '#22a7f0',
              }}
            >
              {filtersInconsistencies.solved === false
                ? i18n.t('app.inconsistencies_page.view_historical')
                : i18n.t('app.inconsistencies_page.view_active')}
            </Typography>
          </Box>
          {hasFilters ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '20px',
                marginLeft: '20px',
                cursor: 'pointer',
              }}
            >
              <Button
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  paddingInline: 3,
                  textTransform: 'uppercase',
                  visibility: hasFilters ? '' : 'hidden',
                  color: '#FF0000',
                  '&:hover': { background: '#FF000022' },
                }}
                startIcon={<img src={DeleteIcon} alt="delete-icon" style={{ width: 14, height: 14 }} />}
                onClick={handleRemoveFilters}
              >
                {i18n.t('app.clear_filters')}
              </Button>
            </Box>
          ) : null}
        </Stack>
      </Box>
      <Box sx={{ marginTop: 2 }}>
        <BaseTable
          rows={filteredRows}
          columns={columns}
          initialPageSize={20}
          pageSizeOptions={[5, 10, 20]}
          getDetailPanelContent={getDetailPanelContent}
          loading={isLoading}
        />
      </Box>
      {openObservationModal && (
        <InconsistenciesObservationModal
          open={openObservationModal}
          setOpen={setOpenObservationModal}
          itemId={resultId}
          setSuccessToast={setSuccessObservations}
        />
      )}
      {successObservations && (
        <ToastAction message={i18n.t('app.inconsistencies_page.success_observation')} severity="success" />
      )}
    </Box>
  );
}
